/**
 * Generic Variant Slider Component JavaScript
 * Handles scrolling navigation and interaction for variant sliders
 */

class VariantSlider extends HTMLElement {
    constructor() {
        super();
        // Initialize properties but don't query DOM yet
        this.container = null;
        this.prevBtn = null;
        this.nextBtn = null;
        this.items = [];
        this.itemWidth = 0;
        this.scrollDistance = 0;
        this.resizeObserver = null;
        this.isLoading = false;
    }

    connectedCallback() {
        // DOM queries happen when element is connected to document
        this.container = this.querySelector(".variant-slider__container");
        this.prevBtn = this.querySelector(".variant-slider__nav--prev");
        this.nextBtn = this.querySelector(".variant-slider__nav--next");
        this.items = this.container?.querySelectorAll(".variant-slider__item") || [];

        if (
            !this.container ||
            !this.prevBtn ||
            !this.nextBtn ||
            this.items.length === 0
        ) {
            return;
        }

        this.setupLoadingStateListeners();
        this.init();
    }

    disconnectedCallback() {
        // Clean up event listeners and observers
        this.resizeObserver?.disconnect();
        window.removeEventListener("resize", this.boundHandleResize);

        // Clean up other event listeners
        if (this.prevBtn && this.boundHandlePrevClick) {
            this.prevBtn.removeEventListener("click", this.boundHandlePrevClick);
        }
        if (this.nextBtn && this.boundHandleNextClick) {
            this.nextBtn.removeEventListener("click", this.boundHandleNextClick);
        }
        if (this.container && this.boundUpdateButtonStates) {
            this.container.removeEventListener("scroll", this.boundUpdateButtonStates);
        }
        if (this.container && this.boundHandleKeyDown) {
            this.container.removeEventListener("keydown", this.boundHandleKeyDown);
        }
        
        // Clean up loading state listeners
        if (this.boundHandleProductInfoStart) {
            document.removeEventListener('product-info:start', this.boundHandleProductInfoStart);
        }
        if (this.boundHandleProductInfoDone) {
            document.removeEventListener('product-info:done', this.boundHandleProductInfoDone);
        }
    }

    init() {
        // Reorder items so in-stock variants come first
        this.reorderItemsByAvailability();

        // Gift products only: if selected flavor is sold out, auto-pick first available flavor.
        this.autoSelectFirstInStockIfNeeded();

        // Calculate scroll distance based on item size
        this.calculateDimensions();

        // Scroll to selected item on initial load
        this.scrollToSelectedItem();

        // Bind event listeners with bound methods for easier cleanup
        this.boundHandlePrevClick = this.handlePrevClick.bind(this);
        this.boundHandleNextClick = this.handleNextClick.bind(this);
        this.boundUpdateButtonStates = this.updateButtonStates.bind(this);
        this.boundHandleResize = this.handleResize.bind(this);

        this.prevBtn.addEventListener("click", this.boundHandlePrevClick);
        this.nextBtn.addEventListener("click", this.boundHandleNextClick);
        this.container.addEventListener("scroll", this.boundUpdateButtonStates);

        // Add keyboard navigation support
        this.boundHandleKeyDown = this.handleKeyDown.bind(this);
        this.container.addEventListener("keydown", this.boundHandleKeyDown);

        // Set ARIA attributes for accessibility
        this.setupAccessibility();

        // Set initial button states
        this.updateButtonStates();

        // Use ResizeObserver for better performance than window resize
        this.setupResizeObserver();
    }

    autoSelectFirstInStockIfNeeded() {
        if (this.dataset.autoSelectFirstInStock !== 'true') {
            return;
        }

        // Run this redirect only once per full page load.
        // If user already changed flavors in-page, never force another redirect.
        if (window.__giftInitialStockRedirectHandled) {
            return;
        }

        const selectedInput = this.querySelector('input[type="radio"]:checked');
        const selectedIsAvailable = selectedInput?.dataset.available === 'true';

        if (selectedIsAvailable) {
            window.__giftInitialStockRedirectHandled = true;
            return;
        }

        const firstAvailableInput = Array.from(this.items)
            .map((item) => item.querySelector('input[type="radio"]'))
            .find((input) => input?.dataset.available === 'true');

        if (!firstAvailableInput || firstAvailableInput === selectedInput) {
            window.__giftInitialStockRedirectHandled = true;
            return;
        }

        window.__giftInitialStockRedirectHandled = true;
        this.setLoadingState(true);
        firstAvailableInput.checked = true;
        firstAvailableInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    calculateDimensions() {
        if (this.items.length > 0) {
            this.itemWidth = this.items[0].offsetWidth + 16; // item width + gap
            this.scrollDistance = this.itemWidth * 2; // scroll by 2 items
        }
    }

    setupResizeObserver() {
        if ('ResizeObserver' in window) {
            this.resizeObserver = new ResizeObserver(() => {
                this.handleResize();
            });
            this.resizeObserver.observe(this.container);
        } else {
            // Fallback for older browsers
            window.addEventListener("resize", this.boundHandleResize);
        }
    }

    handlePrevClick(e) {
        e.preventDefault();
        this.container.scrollBy({
            left: -this.scrollDistance,
            behavior: "smooth",
        });
    }

    handleNextClick(e) {
        e.preventDefault();
        this.container.scrollBy({
            left: this.scrollDistance,
            behavior: "smooth",
        });
    }

    updateButtonStates() {
        const isAtStart = this.container.scrollLeft <= 10;
        const isAtEnd =
            this.container.scrollLeft >=
            this.container.scrollWidth - this.container.clientWidth - 10;

        this.prevBtn.style.opacity = isAtStart ? "0.3" : "1";
        this.nextBtn.style.opacity = isAtEnd ? "0.3" : "1";
        this.prevBtn.style.pointerEvents = isAtStart ? "none" : "auto";
        this.nextBtn.style.pointerEvents = isAtEnd ? "none" : "auto";
    }

    handleResize() {
        // Recalculate dimensions on resize
        this.calculateDimensions();
        this.updateButtonStates();
    }

    // Scroll to the selected item on initial load
    scrollToSelectedItem() {
        // Find the selected item (has variant-slider__item--selected class)
        const selectedItem = this.container.querySelector('.variant-slider__item--selected');
        if (selectedItem) {
            const selectedIndex = Array.from(this.items).indexOf(selectedItem);
            if (selectedIndex !== -1) {
                this.scrollToItem(selectedIndex);
                // Set focus on the selected item for keyboard navigation
                setTimeout(() => {
                    selectedItem.querySelector('input')?.focus();
                }, 100);
            }
        }
    }

    // Public method to reorder items (useful when availability changes)
    reorderItems() {
        this.reorderItemsByAvailability();
        this.calculateDimensions();
        this.updateButtonStates();
    }

    // Public method to scroll to a specific item
    scrollToItem(index) {
        if (index >= 0 && index < this.items.length) {
            const targetScroll = index * this.itemWidth;
            // Center the item in the container for better visibility
            const containerWidth = this.container.clientWidth;
            const centeredScroll = targetScroll - (containerWidth / 2) + (this.itemWidth / 2);
            this.container.scrollTo({left: Math.max(0, centeredScroll), behavior: "smooth"});
        }
    }

    // Reorder items so in-stock variants come first, out-of-stock last
    reorderItemsByAvailability() {
        const availableItems = [];
        const unavailableItems = [];

        // Separate items by availability using data-available attribute
        this.items.forEach(item => {
            const input = item.querySelector('input[type="radio"]');
            const isAvailable = input?.dataset.available === 'true';
            
            if (isAvailable) {
                availableItems.push(item);
            } else {
                unavailableItems.push(item);
            }
        });

        // Clear the container
        const track = this.container.querySelector('.variant-slider__track');
        track.innerHTML = '';

        // Add available items first, then unavailable items
        [...availableItems, ...unavailableItems].forEach(item => {
            track.appendChild(item);
        });

        // Update the items array to reflect new order
        this.items = this.container.querySelectorAll('.variant-slider__item');

        // Add visual indication and ARIA labels for items
        this.items.forEach((item, index) => {
            const input = item.querySelector('input[type="radio"]');
            const isAvailable = input?.dataset.available === 'true';
            
            if (!isAvailable) {
                item.classList.add('variant-slider__item--unavailable');
                item.setAttribute('aria-label', `Out of stock: ${input.value}`);
            } else {
                item.classList.remove('variant-slider__item--unavailable');
                item.setAttribute('aria-label', `Select ${input.value} variant`);
            }
        });
    }

    // Setup accessibility attributes
    setupAccessibility() {
        // Add ARIA role and labels to container
        this.container.setAttribute('role', 'region');
        this.container.setAttribute('aria-label', 'Variant options');

        // Add ARIA labels to navigation buttons
        this.prevBtn.setAttribute('aria-label', 'Previous variant options');
        this.nextBtn.setAttribute('aria-label', 'Next variant options');

        // Add tabindex to make items focusable for keyboard navigation
        this.items.forEach((item, index) => {
            const input = item.querySelector('input[type="radio"]');
            if (input) {
                item.setAttribute('tabindex', input.checked ? '0' : '-1');
                item.setAttribute('role', 'option');
                item.setAttribute('aria-selected', input.checked.toString());

                // Add ARIA label with option value
                const isAvailable = input.dataset.available === 'true';
                if (isAvailable) {
                    item.setAttribute('aria-label', `Select ${input.value} variant`);
                } else {
                    item.setAttribute('aria-label', `Out of stock: ${input.value}`);
                }
            }
        });
    }

    // Handle keyboard navigation
    handleKeyDown(event) {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
            return;
        }

        const focusedElement = document.activeElement;
        const currentItem = focusedElement?.closest('.variant-slider__item');
        if (!currentItem) return;

        const currentIndex = Array.from(this.items).indexOf(currentItem);
        let targetIndex = currentIndex;

        switch (event.key) {
            case 'ArrowLeft':
                targetIndex = Math.max(0, currentIndex - 1);
                break;
            case 'ArrowRight':
                targetIndex = Math.min(this.items.length - 1, currentIndex + 1);
                break;
            case 'Home':
                targetIndex = 0;
                break;
            case 'End':
                targetIndex = this.items.length - 1;
                break;
        }

        if (targetIndex !== currentIndex) {
            event.preventDefault();
            const targetItem = this.items[targetIndex];
            const targetInput = targetItem.querySelector('input[type="radio"]');

            // Update tabindex and ARIA attributes
            this.items.forEach((item, index) => {
                const input = item.querySelector('input[type="radio"]');
                item.setAttribute('tabindex', index === targetIndex ? '0' : '-1');
                item.setAttribute('aria-selected', (index === targetIndex).toString());
            });

            // Focus the target item and scroll it into view
            targetItem.focus();
            this.scrollToItem(targetIndex);

            // Trigger the radio button selection (allow selection of sold-out items too)
            if (targetInput) {
                targetInput.checked = true;
                targetInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }
    }

    // Public method to get currently visible items
    getVisibleItems() {
        const containerRect = this.container.getBoundingClientRect();
        const visibleItems = [];

        this.items.forEach((item, index) => {
            const itemRect = item.getBoundingClientRect();
            if (
                itemRect.right > containerRect.left &&
                itemRect.left < containerRect.right
            ) {
                visibleItems.push({element: item, index});
            }
        });

        return visibleItems;
    }

    /**
     * Setup listeners for product info loading states
     * Disables variant selection during variant changes to prevent race conditions
     */
    setupLoadingStateListeners() {
        this.boundHandleProductInfoStart = this.handleProductInfoStart.bind(this);
        this.boundHandleProductInfoDone = this.handleProductInfoDone.bind(this);

        document.addEventListener('product-info:start', this.boundHandleProductInfoStart);
        document.addEventListener('product-info:done', this.boundHandleProductInfoDone);
    }

    /**
     * Handle product info start - disable variant interactions
     */
    handleProductInfoStart() {
        this.setLoadingState(true);
    }

    /**
     * Handle product info done - re-enable variant interactions
     */
    handleProductInfoDone() {
        this.setLoadingState(false);
    }

    /**
     * Set loading state and disable/enable variant interactions
     * @param {boolean} loading - Whether the slider is in loading state
     */
    setLoadingState(loading) {
        this.isLoading = loading;
        
        // Toggle loading class on the slider for CSS styling
        this.classList.toggle('variant-slider--loading', loading);
        
        // Prevent interactions during loading via pointer-events and visual feedback
        this.items.forEach(item => {
            const label = item.querySelector('label');
            
            if (loading) {
                if (label) {
                    label.style.pointerEvents = 'none';
                }
                item.style.opacity = '0.6';
            } else {
                if (label) {
                    label.style.pointerEvents = '';
                }
                item.style.opacity = '';
            }
        });
    }
}

// Register the custom element
customElements.define('variant-slider', VariantSlider);
